import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import { filter, map } from 'rxjs';
import { environment } from '../environments/environment';
import { AlertComponent } from './components/alert/alert.component';
import { HeaderComponent } from './components/header/header.component';
import { LoaderComponent } from './components/loader/loader.component';
import { SignalBaseDto } from './models/signal/signal-base-dto';
import { UserDto } from './models/user/user-dto';
import { AuthDispatcherService } from './services/local/auth/auth-dispatcher.service';
import { WorkspaceTitleService } from './services/local/workspace-title.service';
import { SignalDispatcherService } from './services/signal/signal-dispatcher.service';
import { SignalHubService } from './services/signal/signal-hub.service';

interface RouteTitleParts {
  title: string;
  parentName: string | null;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [HeaderComponent, LoaderComponent, AlertComponent, RouterOutlet]
})
export class AppComponent {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly signalService = inject(SignalHubService);
  private readonly signalDispatcherService = inject(SignalDispatcherService);
  private readonly authDispatcherService = inject(AuthDispatcherService);
  private readonly workspaceTitleService = inject(WorkspaceTitleService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly routeTitle = signal<RouteTitleParts | null>(null);

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.resolveRouteTitle()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((parts: RouteTitleParts | null) => this.routeTitle.set(parts));

    effect(() => {
      const parts = this.routeTitle();
      const workspaceName = this.workspaceTitleService.workspaceName();
      if (parts) {
        this.titleService.setTitle(this.buildTitle(parts, workspaceName));
      }
    });

    this.subscribeForSignals();
  }

  private resolveRouteTitle(): RouteTitleParts | null {
    let child = this.activatedRoute.firstChild;
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
      } else if (child.snapshot.data && child.snapshot.data['title']) {
        return {
          title: child.snapshot.data['title'],
          parentName: child.snapshot.data['parentName'] ?? null
        };
      } else {
        return null;
      }
    }
    return null;
  }

  private buildTitle(
    parts: RouteTitleParts,
    workspaceName: string | null
  ): string {
    const parentTxt = parts.parentName ? `${parts.parentName} - ` : '';
    if (workspaceName) {
      return `${environment.title}${workspaceName} | ${parentTxt}${parts.title}`;
    }
    return `${environment.title}${parentTxt}${parts.title}`;
  }

  private subscribeForSignals(): void {
    const authSub = this.authDispatcherService.subscribe((_user: UserDto) => {
      void this.signalService
        .connect()
        .then(() => {
          this.signalService
            .getHubConnection()
            .on('processSignal', (msg: SignalBaseDto) => {
              this.signalDispatcherService.publish(msg);
            });
          void this.signalService.register();
        })
        .catch((error: unknown) => {
          console.error('SignalR connection failed:', error);
        });
    });
    this.destroyRef.onDestroy(() => authSub.unsubscribe());
  }
}
